import { type InfiniteData, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef } from "react";

import { useActiveClassroom, useAuthenticatedUserClassrooms } from "@sol/classrooms";
import {
    type INotificationList,
    type INotificationResource,
    NotificationStatus,
    UNREAD_NOTIFICATION_STATUSES,
    useNotificationInfiniteListQuery,
    useNotificationListQuery,
} from "@sol/sdk";
import {
    useNotificationArchiveReadMutation,
    useNotificationMarkAllAsReadMutation,
    useNotificationUpdateMutation,
} from "@sol/sdk/notifications";

import { isNotificationUnread } from "@/entities/notification";

import { getNotificationRedirect } from "../lib/getNotificationRedirect";

const PER_PAGE = 5;
const NOTIFICATIONS_QUERY_KEY = ["notifications"];
const LIST_QUERY_KEY = [...NOTIFICATIONS_QUERY_KEY, "list"];
const UNREAD_QUERY_KEY = [...NOTIFICATIONS_QUERY_KEY, "unread"];
const READ_QUERY_KEY = [...NOTIFICATIONS_QUERY_KEY, "read"];
const COUNT_ONLY = { page: 1, perPage: 1 };

type NotificationPage = INotificationList["hydra:member"];
type LoadedNotifications = InfiniteData<INotificationList>;
type LoadedNotificationsUpdate = (data?: LoadedNotifications) => LoadedNotifications | undefined;

const updateLoadedNotifications =
    (update: (notifications: NotificationPage) => NotificationPage): LoadedNotificationsUpdate =>
    data =>
        data && {
            ...data,
            pages: data.pages.map(page => ({ ...page, "hydra:member": update(page["hydra:member"]) })),
        };

// Pages can shift while scrolling when notifications arrive, so a notification may be returned twice
const flattenNotifications = (pages: INotificationList[] = []) =>
    pages
        .flatMap(page => page["hydra:member"])
        .filter(
            (notification, index, notifications) =>
                notifications.findIndex(({ "@id": id }) => id === notification["@id"]) === index,
        );

type LoadedPosition = { pageIndex: number; index: number };

const withStatus = (notificationId: string, status: NotificationStatus) =>
    updateLoadedNotifications(notifications =>
        notifications.map(notification =>
            notification["@id"] === notificationId ? { ...notification, status } : notification,
        ),
    );

const withoutNotification = (notificationId: string) =>
    updateLoadedNotifications(notifications =>
        notifications.filter(notification => notification["@id"] !== notificationId),
    );

const findLoadedPosition = (
    data: LoadedNotifications | undefined,
    notificationId: string,
): LoadedPosition | undefined =>
    data?.pages
        .map((page, pageIndex) => ({
            pageIndex,
            index: page["hydra:member"].findIndex(({ "@id": id }) => id === notificationId),
        }))
        .find(({ index }) => index >= 0);

// A refresh that succeeded already brought the notification back
const withNotificationRestored =
    (notification: INotificationResource, { pageIndex, index }: LoadedPosition): LoadedNotificationsUpdate =>
    data =>
        data && !flattenNotifications(data.pages).some(({ "@id": id }) => id === notification["@id"])
            ? {
                  ...data,
                  pages: data.pages.map((page, currentPageIndex) =>
                      currentPageIndex === pageIndex
                          ? {
                                ...page,
                                "hydra:member": [
                                    ...page["hydra:member"].slice(0, index),
                                    notification,
                                    ...page["hydra:member"].slice(index),
                                ],
                            }
                          : page,
                  ),
              }
            : data;

// Hiding removes the row, any other status replaces it; undoing restores what was loaded
const getOptimisticListUpdate = (
    data: LoadedNotifications | undefined,
    notificationId: string,
    status: NotificationStatus,
) => {
    const position = findLoadedPosition(data, notificationId);
    const loadedNotification = position && data?.pages[position.pageIndex]["hydra:member"][position.index];

    if (!position || !loadedNotification) {
        return undefined;
    }

    return status === NotificationStatus.HIDDEN
        ? { apply: withoutNotification(notificationId), undo: withNotificationRestored(loadedNotification, position) }
        : { apply: withStatus(notificationId, status), undo: withStatus(notificationId, loadedNotification.status) };
};

type Options = {
    isOpen: boolean;
    onClose: () => void;
    onError: (error: unknown) => void;
};

export const useNotificationMenu = ({ isOpen, onClose, onError }: Options) => {
    const router = useRouter();
    const queryClient = useQueryClient();
    const { classrooms = [] } = useAuthenticatedUserClassrooms();
    const { setActiveClassroom } = useActiveClassroom();

    const unreadNotifications = useNotificationListQuery(
        {
            filters: { status: UNREAD_NOTIFICATION_STATUSES },
            pagination: COUNT_ONLY,
        },
        { queryKey: UNREAD_QUERY_KEY },
    );
    const readNotifications = useNotificationListQuery(
        { filters: { status: [NotificationStatus.READ] }, pagination: COUNT_ONLY },
        { queryKey: READ_QUERY_KEY, enabled: isOpen },
    );
    const list = useNotificationInfiniteListQuery({ perPage: PER_PAGE }, { queryKey: LIST_QUERY_KEY, enabled: isOpen });

    const { refetch: refetchUnreadCount } = unreadNotifications;
    const { fetchNextPage } = list;

    // Stable, as the end-of-list observer is recreated whenever it changes
    const fetchNextPageWithoutCancellingRefresh = useCallback(
        () => fetchNextPage({ cancelRefetch: false }),
        [fetchNextPage],
    );

    // Notifications change on the server while the menu is closed
    useEffect(() => {
        if (isOpen) {
            refetchUnreadCount();
        }
    }, [isOpen, refetchUnreadCount]);

    const pendingUpdates = useRef(0);

    const startUpdate = () => {
        pendingUpdates.current += 1;
    };

    // A refresh while another update is pending would bring its row back until that update settles
    const refreshOnceAllSettled = () => {
        pendingUpdates.current -= 1;

        return pendingUpdates.current === 0
            ? queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
            : undefined;
    };

    const trackedUpdate = { onMutate: startUpdate, onError, onSettled: refreshOnceAllSettled };

    // The undo lives in each mutation context: a mutate call drops the callbacks of the previous one
    const updateNotification = useNotificationUpdateMutation<LoadedNotificationsUpdate | undefined>({
        onMutate: async ({ notification, status }) => {
            startUpdate();
            // A read still in flight would overwrite the optimistic update
            await queryClient.cancelQueries({ queryKey: LIST_QUERY_KEY });
            const notificationId = typeof notification === "string" ? notification : notification["@id"];
            const update = getOptimisticListUpdate(
                queryClient.getQueryData<LoadedNotifications>(LIST_QUERY_KEY),
                notificationId,
                status,
            );

            if (update) {
                queryClient.setQueryData<LoadedNotifications>(LIST_QUERY_KEY, update.apply);
            }

            return update?.undo;
        },
        // Undone before the refresh, which may fail too
        onError: (error, _variables, undo) => {
            if (undo) {
                queryClient.setQueryData<LoadedNotifications>(LIST_QUERY_KEY, undo);
            }

            onError(error);
        },
        onSettled: refreshOnceAllSettled,
    });
    const markAllAsRead = useNotificationMarkAllAsReadMutation(trackedUpdate);
    const archiveRead = useNotificationArchiveReadMutation(trackedUpdate);

    const notifications = flattenNotifications(list.data?.pages);
    const hasLoadedRows = notifications.length > 0;
    // Archiving every loaded row leaves older pages to show once the list refreshes
    const isAwaitingOlderPages = !hasLoadedRows && !!list.hasNextPage && !list.isError;
    // Undefined while the count is unknown, which is not the same as nothing unread
    const unreadCount = unreadNotifications.data?.["hydra:totalItems"];
    const readCount = readNotifications.data?.["hydra:totalItems"];

    const markNotificationAsRead = (notification: INotificationResource) =>
        updateNotification.mutate({ notification, status: NotificationStatus.READ });

    const archiveNotification = (notification: INotificationResource) =>
        updateNotification.mutate({ notification, status: NotificationStatus.HIDDEN });

    const openNotification = (notification: INotificationResource) => {
        if (isNotificationUnread(notification)) {
            markNotificationAsRead(notification);
        }

        const classroom = classrooms.find(({ "@id": id }) => id === notification.recipientClassroom);

        if (classroom) {
            setActiveClassroom(classroom["@id"]);
        }

        onClose();
        router.push(getNotificationRedirect(notification));
    };

    return {
        unreadCount,
        // Loaded rows still tell when the count failed or lags behind
        hasUnread: !!unreadCount || notifications.some(isNotificationUnread),
        hasRead: !!readCount || notifications.some(notification => !isNotificationUnread(notification)),
        notifications,
        // The panel only mounts while open, when the query is enabled
        isLoading: list.isLoading || isAwaitingOlderPages,
        hasLoadError: list.isError && !hasLoadedRows,
        hasNextPageError: list.isError && hasLoadedRows,
        reload: () => Promise.all([list.refetch(), refetchUnreadCount()]),
        isReloading: list.isRefetching && !list.isFetchingNextPage,
        // Loading a page during a refresh cancels it, so pages shifted by an update would skip a row
        canFetchNextPage: !!list.hasNextPage && !list.isFetching && !list.isError,
        isFetchingNextPage: list.isFetchingNextPage,
        // The sentinel can fire before the refresh disables it, and must not cancel that refresh
        fetchNextPage: fetchNextPageWithoutCancellingRefresh,
        isMarkingAllAsRead: markAllAsRead.isPending,
        markAllAsRead: () => markAllAsRead.mutate(),
        isArchivingRead: archiveRead.isPending,
        archiveRead: () => archiveRead.mutate(),
        markNotificationAsRead,
        archiveNotification,
        openNotification,
    };
};
